import Cookies from "js-cookie"
import Router from "next/router"
import { all, call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects"
import { putWait, withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { IAuthReply, IHydraCollection, IUser, IUserObjectRole } from "@api/schema"
import { Routes } from "@basics/routes"
import { refreshTokenAction, setJwtAction } from "@redux/actions/auth"
import { addNotificationAction } from "@redux/actions/notifications"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { newSingleEntityUsecaseRequestRunningAction, newSingleEntityUsecaseRequestSuccessAction } from "@redux/common/entityRequest/actions"
import { usecaseKeyForLoadCollection } from "@redux/common/react-hooks/useEntityCollection"
import { EntityType } from "@redux/common/reduxTypes"
import { loadModelAction, newLoadCollectionAction } from "@redux/common/scopedObject/actions"
import { clearStoreAction } from "@redux/reducer"
import { selectCurrentUserId } from "@redux/reducer/auth"
import { saveAuth } from "@redux/saga/auth"
import { getCurrentUser } from "@redux/saga/currentUser"
import { emptyAuthState } from "@services/authHelper"
import { setStorageItem } from "@services/localStorage"
import { SubmissionError } from "@services/submissionError"
import { AUTH_COOKIE_NAME, AUTH_LOCALSTORAGE_NAME, BASE_URL } from "config"

import { IForgotPasswordAction, ILoadCurrentUserAction, ILoginAction, ILogoutAction, IPostLoginFetchUserDataAction, postLoginFetchUserDataAction } from "./actions"
import { UserAccountUsecases } from "./definitions"

// *************************************************************************************************
// #region user account specific definitions

export function* userAccountWatcherSaga(): any {
  yield all([
    takeLatest(UserAccountUsecases.Login, withCallback(loginSaga)),
    takeLatest(UserAccountUsecases.PostLoginFetchUserData, withCallback(postLoginFetchUserDataSaga)),
    takeEvery(UserAccountUsecases.Logout, logoutSaga), // NOTE originally in saga/auth, and missing the withCallback hook
    takeEvery(UserAccountUsecases.ForgotPassword, forgotPasswordSaga), // NOTE originally in saga/auth, and missing the withCallback hook

    // TODO Warum steht hier ein yield davor, bei den obigen aber nicht? Ist das yield vor dem all() nicht ausreichend?
    yield takeLeading(UserAccountUsecases.LoadCurrentUser, withCallback(loadCurrentUserSaga))
    // yield takeLatest(CurrentUserActionTypes.ChangeEmail, withCallback(changeEmailSaga))
    // yield takeLatest(CurrentUserActionTypes.ChangePassword, withCallback(changePasswordSaga))
    // yield takeLatest(CurrentUserActionTypes.DeleteAccount, withCallback(deleteAccountSaga))
  ])
}

// #endregion


// *************************************************************************************************
// #region login

/**
 * exported for the tests
 */
export function* loginSaga(action: ILoginAction): Generator<any, IAuthReply, any> {
  const { onSuccess, setErrors, setSubmitting } = action.callbackActions || {}

  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  // @todo oauth: disabled b/c no tracking of request-state
  // const usecaseKey = action.type // UserAccountUsecases.Login

  try {
    const auth: IAuthReply = yield call(apiClient.requestAuthToken, action.credentials)

    // NOTE we call the saga directly to ensure that error handling happens here
    yield call(postLoginFetchUserDataSaga, postLoginFetchUserDataAction(auth, onSuccess))

    return auth
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    if (setErrors) {
      if (err instanceof SubmissionError) {
        // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
  }
}

function* postLoginFetchUserDataSaga(action: IPostLoginFetchUserDataAction): Generator<any, void, any> {

  const usecaseKey = action.type // UserAccountUsecases.Login

  try {

    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey))

    yield call(saveAuth, action.authReply)
    // yield put(usecaseRequestSuccessAction(ScopeTypes.UserLogin, auth)) // NOTE formerly we sent the auth object to the scopedRequestReducer ... was it required?

    // notify other tabs to re-check their auth cookie to also login
    yield call(setStorageItem, AUTH_LOCALSTORAGE_NAME, Date.now().toString())

    // loading the current user
    const currentUser: IUser = yield call(getCurrentUser)
    if (!currentUser) {
      throw new Error("user.currentUserNotLoaded")
    }

    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.User, usecaseKey, currentUser))

    // trigger follow-up activities, if onSuccess is defined
    if (action.userLoadedHandler) {
      // return loaded currentUser to the calling onSuccess
      yield call(action.userLoadedHandler, currentUser)
    }
    // trigger the refresh mechanism
    yield put(refreshTokenAction())
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    // if (setErrors) {
    //   if (err instanceof SubmissionError) {
    //     // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
    //     yield call(setErrors, err.errors)
    //   } else {
    //     yield call(setErrors, { error: errorMessage })
    //   }
    // }

    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey, errorMessage))

    // if (setSubmitting) {
    //   yield call(setSubmitting, false)
    // }
  }
}

// #endregion


// *************************************************************************************************
// #region logout
// NOTE selector not needed since we do not call any reducer/state-updating actions
// /**
//  * Returns an (single entity) IRequestState that contains the status of the request.
//  * This special selector exists b/c pages that use logoutAction must not know the usecaseKey
//  * that is internally used (UserAccountUsecases.Logout) - it's a "black box".
//  */
// export const selectLogoutUsecaseState = (state: AppState): IRequestState =>
//   selectSingleEntityUsecaseState(state, EntityType.User, UserAccountUsecases.Logout) // usecaseKey matches the one in logoutAction/Saga

/**
 * exported for the tests
 */
export function* logoutSaga(action: ILogoutAction): Generator<any, void, any> {
  // NOTE usecaseKey not needed since we do not call any reducer/state-updating actions
  // // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  // const usecaseKey = action.type // UserAccountUsecases.Logout

  // redirect before the actual logout, to prevent a flash of the 403 page
  yield call(Router.push, action.redirect)

  // eslint-disable-next-line @typescript-eslint/unbound-method
  yield call(Cookies.remove, AUTH_COOKIE_NAME)

  // Reset the complete store, we don't want anything loaded with higher privileges remaining
  yield put(clearStoreAction())

  // but set auth.initialized to true again so withAuth() works
  yield put(setJwtAction(emptyAuthState))

  // notify other tabs to re-check their auth cookie to also logout
  yield call(setStorageItem, AUTH_LOCALSTORAGE_NAME, Date.now().toString())

  // only after the store was cleared add new state
  yield put(addNotificationAction(action.message || "message.auth.logout", "info", action.options))
}

// #endregion

// *************************************************************************************************
// #region forgot password

function* forgotPasswordSaga(action: IForgotPasswordAction) {
  const { onSuccess, setErrors, setSubmitting } = action.callbackActions || {}

  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  const usecaseKey = action.type // UserAccountUsecases.ForgotPassword

  try {
    // the API uses {{param}} as placeholder while Next uses [param]:
    const route = Routes.ConfirmPasswordReset.replace(/\[/g, "{{").replace(/\]/g, "}}")
    action.data.verificationUrl = BASE_URL + route

    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey))
    const res: boolean = yield call(apiClient.forgotPassword, action.data)

    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.User, usecaseKey, null)) // NOTE/todo returning null; with old requestReducer it was `res`
    if (onSuccess) {
      yield call(onSuccess, res)
    }

    return res
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    if (setErrors) {
      if (err instanceof SubmissionError) {
        // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}

// #endregion


// *************************************************************************************************
// #region load current user object

/**
 * saga to load the current logged in user, identified by its ID
 * NOTE/todo: this saga is not a real saga but more a function that fetches an id and calls another saga.
 * Maybe we'll refactor it accordingly
 */
function* loadCurrentUserSaga(action: ILoadCurrentUserAction) {
  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  const usecaseKey = action.type // UserAccountUsecases.LoadCurrentUser

  const id: number = yield select(selectCurrentUserId)
  if (!id) {
    // NOTE/todo normally we would like to call a *RequestRunningAction(type, usecase) BEFORE calling it again with an error
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey, "failure.notLoggedIn"))
    return null
  }

  const user: IUser = yield putWait(loadModelAction(EntityType.User, id, usecaseKey))

  const userObjectRoles: IHydraCollection<IUserObjectRole> = yield putWait(
    newLoadCollectionAction(
      EntityType.UserObjectRole,
      null,
      usecaseKeyForLoadCollection(null, null, user["@id"]),
      user["@id"],
      true /* loadAll */
    )
  )
  if (!userObjectRoles) {
    // NOTE/todo normally we would like to call a *RequestRunningAction(type, usecase) BEFORE calling it again with an error
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey, "user.userObjectRolesNotLoaded"))
    return null
  }

  return user

}

// #endregion